import { useEffect, useRef, useState, type ChangeEvent } from "react";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import type { ApprovalStatus, Distributor } from "@/types";
import NumberInput from "@/components/base/NumberInput";
import type { AdvisorAccount } from "@/utils/accounting";
import { formatCOP, formatNumber } from "@/utils/format";
import AdvisorReceivedGoods from "@/pages/admin/asesores/components/AdvisorReceivedGoods";
import AdvisorRestockList from "@/pages/admin/asesores/components/AdvisorRestockList";

interface AdvisorMasterModalProps {
  open: boolean;
  advisor: Distributor | null;
  account?: AdvisorAccount | null;
  onClose: () => void;
  onSave: (d: Distributor) => void;
  onSetStatus: (id: string, status: ApprovalStatus) => void;
  onDelete: (id: string) => void;
}

const MAX_DOC_CHARS = 600000; // límite prudente (~450 KB) para la imagen de cédula

interface FormState {
  names: string;
  surnames: string;
  cedula: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  margin: 20 | 25;
  consignmentLimit: number;
  idFront?: string;
  idBack?: string;
  refName: string;
  refRelationship: string;
  refPhone: string;
  refCity: string;
}

const fromAdvisor = (d: Distributor): FormState => ({
  names: d.names,
  surnames: d.surnames,
  cedula: d.cedula,
  phone: d.phone,
  email: d.email,
  address: d.address,
  city: d.city,
  margin: d.margin,
  consignmentLimit: d.consignmentLimit,
  idFront: d.idFront,
  idBack: d.idBack,
  refName: d.reference.name,
  refRelationship: d.reference.relationship,
  refPhone: d.reference.phone,
  refCity: d.reference.city,
});

const AdvisorMasterModal = ({ open, advisor, account, onClose, onSave, onSetStatus, onDelete }: AdvisorMasterModalProps) => {
  const [form, setForm] = useState<FormState | null>(null);
  const [docError, setDocError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const frontRef = useRef<HTMLInputElement>(null);
  const backRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && advisor) {
      setForm(fromAdvisor(advisor));
      setDocError("");
      setConfirmDelete(false);
    }
  }, [open, advisor]);

  // Paz y salvo: no se puede eliminar al asesor hasta que haya entregado toda la
  // mercancía (consignación y propia) y no tenga deuda pendiente.
  const cleanConsigned = (account?.consignedUnits ?? 0) === 0 && (account?.ownedUnits ?? 0) === 0;
  const cleanDebt = (account?.debt ?? 0) <= 0;
  const settled = account ? cleanConsigned && cleanDebt : true;

  if (!form) {
    return (
      <Modal open={open} onClose={onClose} title="Ficha máster del asesor" icon="ri-id-card-line">
        <p className="text-sm text-foreground-600">Selecciona un asesor.</p>
      </Modal>
    );
  }

  const set = (patch: Partial<FormState>) => setForm((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleFile = (side: "front" | "back") => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setDocError("El archivo debe ser una imagen (JPG o PNG).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      if (result.length > MAX_DOC_CHARS) {
        setDocError("La imagen es muy pesada. Usa una foto más liviana (menos de ~450 KB).");
        return;
      }
      setDocError("");
      set(side === "front" ? { idFront: result } : { idBack: result });
    };
    reader.readAsDataURL(file);
  };

  const submit = () => {
    if (!advisor) return;
    const updated: Distributor = {
      ...advisor,
      names: form.names.trim(),
      surnames: form.surnames.trim(),
      cedula: form.cedula.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      margin: form.margin,
      consignmentLimit: Number(form.consignmentLimit) || 0,
      idFront: form.idFront,
      idBack: form.idBack,
      reference: {
        name: form.refName.trim(),
        relationship: form.refRelationship.trim(),
        phone: form.refPhone.trim(),
        city: form.refCity.trim(),
      },
    };
    onSave(updated);
  };

  const verified = Boolean(form.idFront && form.cedula);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Ficha máster · ${advisor?.names ?? ""}`}
      icon="ri-id-card-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} className={primaryButtonClass}>
            <i className="ri-save-3-line" /> Guardar ficha
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full ${
              verified
                ? "bg-accent-500/20 text-accent-200"
                : "bg-secondary-500/20 text-secondary-100"
            }`}
          >
            <i className={verified ? "ri-shield-check-line" : "ri-shield-line"} />
            {verified ? "Identidad verificada" : "Verificación pendiente"}
          </span>
          <span className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-background-200/60 text-foreground-700">
            Margen {form.margin}%
          </span>
          {advisor && advisor.approvalStatus !== "approved" && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-full bg-primary-500/20 text-primary-100">
              <i className="ri-pause-circle-line" />
              {advisor.approvalStatus === "suspended"
                ? "Acceso suspendido"
                : advisor.approvalStatus === "rejected"
                ? "Registro rechazado"
                : "Pendiente de aprobación"}
            </span>
          )}
          {advisor && (
            <div className="ml-auto flex items-center gap-2">
              {(advisor.approvalStatus === "approved" || advisor.approvalStatus === "suspended") && (
                <button
                  onClick={() =>
                    onSetStatus(
                      advisor.id,
                      advisor.approvalStatus === "suspended" ? "approved" : "suspended"
                    )
                  }
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer border ${
                    advisor.approvalStatus === "suspended"
                      ? "border-accent-500/40 text-accent-200 hover:bg-accent-500/10"
                      : "border-primary-400/40 text-primary-100 hover:bg-primary-500/10"
                  }`}
                >
                  <i
                    className={
                      advisor.approvalStatus === "suspended"
                        ? "ri-play-circle-line"
                        : "ri-pause-circle-line"
                    }
                  />
                  {advisor.approvalStatus === "suspended" ? "Reactivar acceso" : "Suspender acceso"}
                </button>
              )}
              {confirmDelete ? (
                <span className="inline-flex items-center gap-1.5">
                  <button
                    onClick={() => {
                      onDelete(advisor.id);
                      setConfirmDelete(false);
                    }}
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer border border-primary-400/60 bg-primary-500/20 text-primary-100 hover:bg-primary-500/30"
                  >
                    <i className="ri-delete-bin-6-line" /> Sí, eliminar
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-foreground-500 hover:text-foreground-800 cursor-pointer"
                  >
                    Cancelar
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={!settled}
                  title={
                    settled
                      ? "Eliminar la cuenta del asesor por completo"
                      : "No se puede eliminar: el asesor debe estar a paz y salvo (sin mercancía entregada pendiente y sin deuda)"
                  }
                  className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-md whitespace-nowrap border ${
                    settled
                      ? "border-primary-400/40 text-primary-100 hover:bg-primary-500/10 cursor-pointer"
                      : "border-background-300/40 text-foreground-500 cursor-not-allowed opacity-60"
                  }`}
                >
                  <i className="ri-delete-bin-6-line" /> Eliminar asesor
                </button>
              )}
            </div>
          )}
        </div>

        {/* Resumen de cuenta */}
        {account && (
          <div className="rounded-lg border border-accent-500/25 bg-background-200/30 p-3.5">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-[11px] font-bold uppercase tracking-wider text-accent-300">
                Resumen de su cuenta
              </h4>
              <span className="text-[10px] text-foreground-500">
                Cupo de consignación: {formatCOP(account.advisor.consignmentLimit)}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
                <p className="text-[10px] text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-shopping-bag-3-line text-primary-200" /> Compró
                </p>
                <p className="text-sm font-bold text-foreground-950 mt-0.5">
                  {formatCOP(account.compradoValue)}
                </p>
                <p className="text-[10px] text-foreground-500">
                  {formatNumber(account.compradoUnits)} und
                </p>
              </div>
              <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
                <p className="text-[10px] text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-hand-heart-line text-secondary-200" /> En consignación
                </p>
                <p className="text-sm font-bold text-secondary-200 mt-0.5">
                  {formatCOP(account.consignedValue)}
                </p>
                <p className="text-[10px] text-foreground-500">
                  {formatNumber(account.consignedUnits)} und
                </p>
              </div>
              <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
                <p className="text-[10px] text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-hand-coin-line text-accent-200" /> Debe al admin
                </p>
                <p
                  className={`text-sm font-bold mt-0.5 ${
                    account.debt > 0 ? "text-accent-200" : "text-foreground-700"
                  }`}
                >
                  {formatCOP(account.debt)}
                </p>
                <p className="text-[10px] text-foreground-500">saldo pendiente</p>
              </div>
            </div>
            {account.consignmentLimit > 0 && account.consignedValue > account.consignmentLimit && (
              <p className="text-[10px] text-primary-200 mt-2 flex items-center gap-1.5">
                <i className="ri-alert-line" /> Supera su cupo de consignación.
              </p>
            )}
          </div>
        )}

        {/* Mercancía recibida: consignación y compras, por producto */}
        {account && <AdvisorReceivedGoods movements={account.movements} />}

        {/* Paz y salvo: aviso cuando el asesor NO se puede eliminar todavía. */}
        {advisor && !settled && (
          <div className="rounded-md bg-secondary-500/10 border border-secondary-500/30 px-3.5 py-2.5 flex items-start gap-2.5">
            <span className="w-8 h-8 rounded-md bg-secondary-500/25 text-secondary-100 flex items-center justify-center shrink-0">
              <i className="ri-lock-line" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-secondary-100">Para eliminar debe estar a paz y salvo</p>
              <p className="text-[11px] text-foreground-700 mt-0.5">
                {!cleanConsigned &&
                  `Aún tiene mercancía entregada (${formatNumber((account?.consignedUnits ?? 0) + (account?.ownedUnits ?? 0))} und). `}
                {!cleanDebt && `Debe ${formatCOP(account?.debt ?? 0)}. `}
                Cuando entregue el producto y salde la deuda, podrás eliminar su cuenta.
              </p>
            </div>
          </div>
        )}

        {/* Lista de reposición para el sábado */}
        {advisor && <AdvisorRestockList advisorId={advisor.id} />}

        {/* Identidad */}
        <div>
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
            Datos de identidad
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nombres</label>
              <input
                value={form.names}
                onChange={(e) => set({ names: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Apellidos</label>
              <input
                value={form.surnames}
                onChange={(e) => set({ surnames: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Cédula</label>
              <input
                value={form.cedula}
                onChange={(e) => set({ cedula: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Celular</label>
              <input
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Correo</label>
              <input
                value={form.email}
                onChange={(e) => set({ email: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Ciudad</label>
              <input
                value={form.city}
                onChange={(e) => set({ city: e.target.value })}
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Dirección</label>
              <input
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Comercial */}
        <div>
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
            Condiciones comerciales
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Margen comercial</label>
              <select
                value={form.margin}
                onChange={(e) => set({ margin: Number(e.target.value) === 20 ? 20 : 25 })}
                className={inputClass}
              >
                <option value={25} className="bg-background-100">
                  Asesor nuevo · 25%
                </option>
                <option value={20} className="bg-background-100">
                  Asesor fundador · 20%
                </option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Cupo de consignación (COP)</label>
              <NumberInput
                min={0}
                value={form.consignmentLimit}
                onChange={(n) => set({ consignmentLimit: n })}
                className={inputClass}
              />
              <p className="text-[10px] text-foreground-500 mt-1">
                {formatCOP(Number(form.consignmentLimit) || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Verificación de identidad */}
        <div>
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
            Verificación de identidad (cédula)
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(
              [
                { side: "front" as const, label: "Frente de la cédula", value: form.idFront, ref: frontRef },
                { side: "back" as const, label: "Reverso de la cédula", value: form.idBack, ref: backRef },
              ]
            ).map((doc) => (
              <div key={doc.side} className="rounded-md border border-background-300/40 p-3">
                <p className="text-[11px] font-semibold text-foreground-600 mb-2">{doc.label}</p>
                <div className="w-full h-32 rounded-md bg-background-200/40 border border-background-300/40 flex items-center justify-center overflow-hidden mb-2">
                  {doc.value ? (
                    <img
                      src={doc.value}
                      alt={doc.label}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <span className="text-[11px] text-foreground-500 flex flex-col items-center gap-1">
                      <i className="ri-image-add-line text-xl" /> Sin documento
                    </span>
                  )}
                </div>
                <input
                  ref={doc.ref}
                  type="file"
                  accept="image/*"
                  onChange={handleFile(doc.side)}
                  className="hidden"
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => doc.ref.current?.click()}
                    className="px-3 py-1.5 rounded-md border border-accent-500/35 text-accent-200 hover:bg-accent-500/10 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <i className="ri-upload-2-line" /> {doc.value ? "Cambiar" : "Cargar"}
                  </button>
                  {doc.value && (
                    <button
                      onClick={() =>
                        set(doc.side === "front" ? { idFront: undefined } : { idBack: undefined })
                      }
                      className="px-3 py-1.5 rounded-md text-foreground-500 hover:text-primary-200 text-[11px] font-semibold cursor-pointer"
                    >
                      Quitar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {docError && (
            <p className="text-[11px] text-primary-200 mt-2 flex items-center gap-1.5">
              <i className="ri-error-warning-line" /> {docError}
            </p>
          )}
        </div>

        {/* Referencia */}
        <div>
          <h4 className="text-[11px] font-bold uppercase tracking-wider text-accent-300 mb-2">
            Referencia personal
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Nombre</label>
              <input
                value={form.refName}
                onChange={(e) => set({ refName: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Parentesco</label>
              <input
                value={form.refRelationship}
                onChange={(e) => set({ refRelationship: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Celular</label>
              <input
                value={form.refPhone}
                onChange={(e) => set({ refPhone: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Ciudad</label>
              <input
                value={form.refCity}
                onChange={(e) => set({ refCity: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AdvisorMasterModal;